import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  MapPin,
  TrendingUp,
  Settings,
  Eye,
  Copy,
  Archive,
  Filter,
  Download,
  Upload,
  X,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../../hooks/useTheme';

interface Well {
  id: string;
  name: string;
  location: string;
  company: string;
  status: 'active' | 'inactive' | 'maintenance' | 'critical';
  oilProduction?: number;
  waterProduction?: number;
  staticPressure?: number;
  lastEntry?: string;
  createdAt: string;
  createdBy: string;
}

interface AdminWellsManagerProps {
  wells: Well[];
  onEditWell: (well: Well) => void;
  onDeleteWell: (wellId: string) => void;
  onCreateWell: () => void;
  onViewWell: (well: Well) => void;
}

export default function AdminWellsManager({
  wells,
  onEditWell,
  onDeleteWell,
  onCreateWell,
  onViewWell,
}: AdminWellsManagerProps) {
  const { isDarkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | Well['status']>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'status'>('name');

  const filteredWells = wells
    .filter((well) => {
      if (filterStatus !== 'all' && well.status !== filterStatus) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          well.name.toLowerCase().includes(query) ||
          well.location?.toLowerCase().includes(query) ||
          well.company?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  const handleDuplicate = (well: Well) => {
    const duplicate = {
      ...well,
      id: `${well.id}-copy`,
      name: `${well.name} (Copy)`,
      createdAt: new Date().toISOString(),
    };
    toast.success('Well duplicated');
    onEditWell(duplicate);
  };

  const handleBulkImport = () => {
    toast.info('Bulk import feature coming soon');
  };

  const handleExport = () => {
    // Export wells to CSV
    const csv = [
      ['Name', 'Location', 'Company', 'Status', 'Created'],
      ...filteredWells.map((w) => [
        w.name,
        w.location,
        w.company,
        w.status,
        new Date(w.createdAt).toLocaleDateString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wells-export-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Wells exported successfully');
  };

  const getStatusColor = (status: Well['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'critical':
        return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b p-4 space-y-3 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Well Management</h2>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{wells.length} total wells</p>
          </div>
          <Button onClick={onCreateWell} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-5 w-5 mr-2" />
            Add Well
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <Input
            type="text"
            placeholder="Search wells..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-10 pr-10 h-12 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
            >
              <X className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-hide">
          <Filter className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          {(['all', 'active', 'inactive', 'maintenance', 'critical'] as const).map(
            (status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : isDarkMode 
                      ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            )
          )}
        </div>

        {/* Actions Bar */}
        <div className="flex items-center space-x-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className={`flex-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'border-gray-300'}`}
          >
            <option value="name">Sort by Name</option>
            <option value="created">Sort by Created</option>
            <option value="status">Sort by Status</option>
          </select>

          <button
            onClick={handleExport}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
            title="Export Wells"
          >
            <Download className={`h-5 w-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>

          <button
            onClick={handleBulkImport}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
            title="Import Wells"
          >
            <Upload className={`h-5 w-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className={`px-4 py-2 border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Showing {filteredWells.length} of {wells.length} wells
        </p>
      </div>

      {/* Wells List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredWells.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Settings className={`h-16 w-16 mb-4 ${isDarkMode ? 'text-slate-700' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No wells found</h3>
            <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {searchQuery
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first well'}
            </p>
            {!searchQuery && (
              <Button onClick={onCreateWell} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-5 w-5 mr-2" />
                Create First Well
              </Button>
            )}
          </div>
        ) : (
          filteredWells.map((well) => (
            <Card key={well.id} className={`hover:shadow-md transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0 pr-3">
                    <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{well.name}</h3>
                    <div className={`flex items-center space-x-2 text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{well.location || 'No location'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(well.status)}>{well.status}</Badge>
                      {well.company && (
                        <Badge variant="outline" className="text-xs">
                          {well.company}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Production Metrics (if available) */}
                {(well.oilProduction || well.waterProduction || well.staticPressure) && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {well.oilProduction !== undefined && (
                      <div className="bg-blue-50 rounded p-2">
                        <p className="text-[10px] text-blue-700 uppercase">Oil</p>
                        <p className="text-sm font-bold text-blue-900">
                          {well.oilProduction}
                        </p>
                        <p className="text-[10px] text-blue-600">BPD</p>
                      </div>
                    )}
                    {well.waterProduction !== undefined && (
                      <div className="bg-cyan-50 rounded p-2">
                        <p className="text-[10px] text-cyan-700 uppercase">Water</p>
                        <p className="text-sm font-bold text-cyan-900">
                          {well.waterProduction}
                        </p>
                        <p className="text-[10px] text-cyan-600">BPD</p>
                      </div>
                    )}
                    {well.staticPressure !== undefined && (
                      <div className="bg-purple-50 rounded p-2">
                        <p className="text-[10px] text-purple-700 uppercase">Pressure</p>
                        <p className="text-sm font-bold text-purple-900">
                          {well.staticPressure}
                        </p>
                        <p className="text-[10px] text-purple-600">PSI</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Metadata */}
                <div className={`text-xs mb-3 space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="flex items-center justify-between">
                    <span>Created:</span>
                    <span>{new Date(well.createdAt).toLocaleDateString()}</span>
                  </div>
                  {well.createdBy && (
                    <div className="flex items-center justify-between">
                      <span>Created by:</span>
                      <span>{well.createdBy}</span>
                    </div>
                  )}
                  {well.lastEntry && (
                    <div className="flex items-center justify-between">
                      <span>Last entry:</span>
                      <span>{new Date(well.lastEntry).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className={`grid grid-cols-4 gap-2 pt-3 border-t ${isDarkMode ? 'border-slate-700' : ''}`}>
                  <button
                    onClick={() => onViewWell(well)}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 active:bg-slate-700' : 'hover:bg-gray-50 active:bg-gray-100'}`}
                  >
                    <Eye className={`h-5 w-5 mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>View</span>
                  </button>

                  <button
                    onClick={() => onEditWell(well)}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-blue-900/30 active:bg-blue-900/50' : 'hover:bg-blue-50 active:bg-blue-100'}`}
                  >
                    <Edit className="h-5 w-5 text-blue-600 mb-1" />
                    <span className="text-[10px] text-blue-700">Edit</span>
                  </button>

                  <button
                    onClick={() => handleDuplicate(well)}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-green-900/30 active:bg-green-900/50' : 'hover:bg-green-50 active:bg-green-100'}`}
                  >
                    <Copy className="h-5 w-5 text-green-600 mb-1" />
                    <span className="text-[10px] text-green-700">Copy</span>
                  </button>

                  <button
                    onClick={() => {
                      if (confirm(`Delete ${well.name}?`)) {
                        onDeleteWell(well.id);
                        toast.success('Well deleted');
                      }
                    }}
                    className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-900/30 active:bg-red-900/50' : 'hover:bg-red-50 active:bg-red-100'}`}
                  >
                    <Trash2 className="h-5 w-5 text-red-600 mb-1" />
                    <span className="text-[10px] text-red-700">Delete</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Stats Footer */}
      <div className={`border-t p-4 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white'}`}>
        <div className="grid grid-cols-4 gap-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {wells.filter((w) => w.status === 'active').length}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {wells.filter((w) => w.status === 'maintenance').length}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Maintenance</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">
              {wells.filter((w) => w.status === 'critical').length}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Critical</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {wells.filter((w) => w.status === 'inactive').length}
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</p>
          </div>
        </div>
      </div>
    </div>
  );
}
